import { Component } from '@theme/component';

const STAR_PATH =
  'M31.547 12a.848.848 0 00-.677-.577l-9.427-1.376-4.224-8.532a.847.847 0 00-1.516 0l-4.218 8.534-9.427 1.355a.847.847 0 00-.467 1.467l6.823 6.664-1.612 9.375a.847.847 0 001.23.893l8.428-4.434 8.432 4.432a.847.847 0 001.229-.894l-1.615-9.373 6.822-6.665a.845.845 0 00.214-.869z';

const CAROUSEL_NAV_PREV_SVG =
  '<svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M11.0019 0.984688C10.5119 0.494687 9.72189 0.494687 9.23189 0.984688L0.921895 9.29469C0.531895 9.68469 0.531895 10.3147 0.921895 10.7047L9.23189 19.0147C9.72189 19.5047 10.5119 19.5047 11.0019 19.0147C11.4919 18.5247 11.4919 17.7347 11.0019 17.2447L3.76189 9.99469L11.0119 2.74469C11.4919 2.26469 11.4919 1.46469 11.0019 0.984688Z" fill="currentColor"/></svg>';

const CAROUSEL_NAV_NEXT_SVG =
  '<svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M0.998154 19.0147C1.48815 19.5047 2.27815 19.5047 2.76815 19.0147L11.0782 10.7047C11.4682 10.3147 11.4682 9.68469 11.0782 9.29469L2.76815 0.984688C2.27815 0.494687 1.48815 0.494687 0.998154 0.984688C0.508154 1.47469 0.508154 2.26469 0.998154 2.75469L8.23815 10.0047L0.988154 17.2547C0.508154 17.7347 0.508154 18.5347 0.998154 19.0147Z" fill="currentColor"/></svg>';

/**
 * @param {string} value
 */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @param {string} value
 */
function escapeAttr(value) {
  return escapeHtml(value).replace(/'/g, '&#39;');
}

/**
 * @param {string} dateStr
 * @param {{ show?: boolean; format?: string }} [options]
 */
function formatReviewDate(dateStr, options = {}) {
  const { show = true, format = 'us' } = options;
  if (!show || !dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return format === 'european' ? `${day}/${month}/${year}` : `${month}/${day}/${year}`;
}

/**
 * @param {string} dateStr
 * @param {{ showDate?: string; dateFormat?: string }} dataset
 * @param {(value: string) => string} escapeAttrFn
 * @param {string} [className]
 */
function renderReviewDateHtml(dateStr, dataset, escapeAttrFn, className = 'customer-reviews__review-date') {
  if (dataset.showDate !== 'true' || !dateStr) return '';
  const formatted = formatReviewDate(dateStr, {
    show: true,
    format: dataset.dateFormat === 'european' ? 'european' : 'us',
  });
  if (!formatted) return '';
  return `<time class="${className}" datetime="${escapeAttrFn(dateStr)}">${formatted}</time>`;
}

/**
 * @param {string} name
 */
function getReviewInitial(name) {
  return (name?.trim()?.charAt(0) || '?').toUpperCase();
}

/**
 * @param {Element | null} element
 * @returns {Record<string, string>}
 */
function getCustomerReviewsI18n(element) {
  const section = element?.closest('.customer-reviews-section');
  const script = section?.querySelector('[data-customer-reviews-i18n]');
  if (!script?.textContent?.trim()) return {};

  try {
    const parsed = JSON.parse(script.textContent);
    return parsed && typeof parsed === 'object' ? /** @type {Record<string, string>} */ (parsed) : {};
  } catch {
    return {};
  }
}

/**
 * @param {Record<string, string>} i18n
 * @param {string} key
 * @param {Record<string, string | number>} [replacements]
 */
function translateI18n(i18n, key, replacements = {}) {
  let str = i18n[key] ?? '';
  for (const [replacementKey, value] of Object.entries(replacements)) {
    str = str.replace(new RegExp(`\\{\\{\\s*${replacementKey}\\s*\\}\\}`, 'g'), String(value));
    str = str.replace(new RegExp(`\\[${replacementKey}\\]`, 'g'), String(value));
  }
  return str;
}

/**
 * @param {number} rating
 * @param {string} [sizeClass]
 * @param {string} [ariaLabel]
 */
function renderStarRating(rating, sizeClass = '', ariaLabel = '') {
  const filled = Math.min(5, Math.max(0, Math.round(Number(rating) || 0)));
  const stars = Array.from({ length: 5 }, (_, index) => {
    const filledClass = index < filled ? ' customer-reviews__star--filled' : '';
    return `<svg class="customer-reviews__star${filledClass} ${sizeClass}" viewBox="0 0 32 32" aria-hidden="true"><path d="${STAR_PATH}"></path></svg>`;
  }).join('');

  const ariaAttr = ariaLabel ? ` aria-label="${escapeAttr(ariaLabel)}"` : '';
  return `<span class="customer-reviews__stars" role="img"${ariaAttr}>${stars}</span>`;
}

/**
 * @typedef {Object} Question
 * @property {string} id
 * @property {string} author
 * @property {string} body
 * @property {string} date
 * @property {{ author: string; date: string; body: string }} [reply]
 */

/**
 * Ensure each review image URL appears on at most one review (first wins).
 * @param {Review[]} reviews
 * @returns {Review[]}
 */
function dedupeReviewImages(reviews) {
  const usedImages = new Set();

  return reviews.map((review) => {
    if (!review.images?.length) return review;

    const images = review.images.filter((image) => {
      if (usedImages.has(image)) return false;
      usedImages.add(image);
      return true;
    });

    if (!images.length) {
      const { images: _removed, ...rest } = review;
      return /** @type {Review} */ (rest);
    }

    return { ...review, images };
  });
}

/**
 * @typedef {Object} Review
 * @property {string} id
 * @property {string} author
 * @property {number} rating
 * @property {string} body
 * @property {string} date
 * @property {string[]} [images]
 */

/**
 * @typedef {Object} CustomerReviewsRefs
 * @property {HTMLElement} [container]
 */

/**
 * Judge.me-style customer reviews widget powered by theme JSON.
 *
 * @extends {Component<CustomerReviewsRefs>}
 */
class CustomerReviews extends Component {
  /** @type {Review[]} */
  #allReviews = [];

  /** @type {Question[]} */
  #allQuestions = [];

  /** @type {{ title?: string; text?: string; disclaimer?: string } | null} */
  #aiSummary = null;

  /** @type {'reviews' | 'questions'} */
  #activeTab = 'reviews';

  /** @type {string} */
  #sortKey = 'created_at';

  /** @type {string} */
  #ratingFilter = 'all';

  /** @type {boolean} */
  #filterOpen = false;

  /** @type {number} */
  #currentPage = 1;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {AbortController | null} */
  #uiAbortController = null;

  /** @type {Record<string, string> | null} */
  #i18n = null;

  /** @returns {Record<string, string>} */
  #getI18n() {
    if (!this.#i18n) {
      this.#i18n = getCustomerReviewsI18n(this);
    }
    return this.#i18n;
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [replacements]
   */
  #t(key, replacements = {}) {
    return translateI18n(this.#getI18n(), key, replacements);
  }

  #buildFilterOptions() {
    const i18n = this.#getI18n();
    return [
      { value: 'all', label: i18n.filter_all ?? '' },
      { value: '5', label: i18n.filter_5_stars ?? '' },
      { value: '4', label: i18n.filter_4_stars ?? '' },
      { value: '3', label: i18n.filter_3_stars ?? '' },
      { value: '2', label: i18n.filter_2_stars ?? '' },
      { value: '1', label: i18n.filter_1_star ?? '' },
    ];
  }

  #buildSortOptions() {
    const i18n = this.#getI18n();
    return [
      { value: 'created_at', label: i18n.sort_latest ?? '' },
      { value: 'highest_rating', label: i18n.sort_highest ?? '' },
      { value: 'lowest_rating', label: i18n.sort_lowest ?? '' },
      { value: 'with_pictures', label: i18n.sort_pictures_only ?? '' },
      { value: 'pictures_first', label: i18n.sort_images_first ?? '' },
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handlePaginationClick);
    this.#loadReviews();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handlePaginationClick);
    this.#abortController?.abort();
    this.#uiAbortController?.abort();
  }

  async #loadReviews() {
    const url = this.dataset.jsonUrl;
    if (!url) {
      this.#renderError(this.#t('error_missing_url'));
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      this.#allReviews = dedupeReviewImages(Array.isArray(data.reviews) ? data.reviews : []);
      this.#allQuestions = Array.isArray(data.questions) ? data.questions : [];
      this.#aiSummary = data.ai_summary && typeof data.ai_summary === 'object' ? data.ai_summary : null;
      if (this.#activeTab === 'questions' && this.#allQuestions.length === 0) {
        this.#activeTab = 'reviews';
      }
      this.#currentPage = 1;
      this.#render();
      this.#dispatchStats();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#renderError(this.#t('error_load_failed'));
    }
  }

  #dispatchStats() {
    const stats = this.#computeStats(this.#allReviews);
    this.dataset.averageRating = String(stats.average);
    this.dataset.reviewCount = String(stats.total);

    this.dispatchEvent(
      new CustomEvent('customer-reviews:loaded', {
        bubbles: true,
        detail: stats,
      })
    );
  }

  /**
   * @param {Review[]} reviews
   */
  #computeStats(reviews) {
    const total = reviews.length;
    if (total === 0) {
      return {
        average: 0,
        total: 0,
        histogram: [5, 4, 3, 2, 1].map((rating) => ({ rating, frequency: 0, percentage: 0 })),
        photoGallery: [],
      };
    }

    const sum = reviews.reduce((acc, review) => acc + (Number(review.rating) || 0), 0);
    const average = Math.round((sum / total) * 10) / 10;

    /** @type {Record<number, number>} */
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    for (const review of reviews) {
      const rating = Math.min(5, Math.max(1, Math.round(Number(review.rating) || 0)));
      counts[rating] += 1;
    }

    const histogram = [5, 4, 3, 2, 1].map((rating) => ({
      rating,
      frequency: counts[rating],
      percentage: total ? Math.round((counts[rating] / total) * 100) : 0,
    }));

    /** @type {{ image: string; review: Review }[]} */
    const photoGallery = [];
    const seen = new Set();

    for (const review of reviews) {
      if (!review.images?.length) continue;
      for (const image of review.images) {
        if (seen.has(image)) continue;
        seen.add(image);
        photoGallery.push({ image, review });
        if (photoGallery.length >= 8) break;
      }
      if (photoGallery.length >= 8) break;
    }

    return { average, total, histogram, photoGallery };
  }

  /**
   * @param {Review[]} reviews
   */
  #filterReviews(reviews) {
    switch (this.#ratingFilter) {
      case '5':
      case '4':
      case '3':
      case '2':
      case '1':
        return reviews.filter((review) => Number(review.rating) === Number(this.#ratingFilter));
      case 'all':
      default:
        return reviews;
    }
  }

  /**
   * @param {Question[]} questions
   */
  #sortQuestions(questions) {
    return [...questions].sort((a, b) => b.date.localeCompare(a.date));
  }

  #renderFilterPanel() {
    return `
      <div class="customer-reviews__filter-panel" data-filter-panel${this.#filterOpen ? '' : ' hidden'}>
        ${this.#buildFilterOptions()
          .map(
            (option) => `
            <button
              type="button"
              class="customer-reviews__filter-option${option.value === this.#ratingFilter ? ' customer-reviews__filter-option--active' : ''}"
              data-filter-value="${option.value}"
            >
              ${option.label}
            </button>
          `
          )
          .join('')}
      </div>
    `;
  }

  /**
   * @param {Question[]} questions
   */
  #renderQuestionsList(questions) {
    const shopName = this.dataset.shopName || '';

    if (!questions.length) {
      return `<p class="customer-reviews__empty">${this.#escapeHtml(this.#t('no_questions'))}</p>`;
    }

    return questions
      .map((question) => {
        const reply = question.reply;
        const replyHtml = reply
          ? `
            <div class="customer-reviews__question-reply">
              <div class="customer-reviews__question-reply-header">
                <span>${this.#escapeHtml(this.#t('question_reply_label', { author: reply.author || shopName }))}</span>
                ${this.#renderDateHtml(reply.date)}
              </div>
              <p class="customer-reviews__question-reply-body">${this.#escapeHtml(reply.body)}</p>
            </div>
          `
          : '';

        return `
          <article class="customer-reviews__question">
            <div class="customer-reviews__reviewer">
              <span class="customer-reviews__avatar" aria-hidden="true">${this.#escapeHtml(this.#getInitial(question.author))}</span>
              <div class="customer-reviews__reviewer-details">
                <span class="customer-reviews__reviewer-name">${this.#escapeHtml(question.author)}</span>
                ${this.#renderDateHtml(question.date)}
              </div>
            </div>
            <p class="customer-reviews__question-body">${this.#escapeHtml(question.body)}</p>
            ${replyHtml}
          </article>
        `;
      })
      .join('');
  }

  /**
   * @param {Review[]} reviews
   * @param {string} sortKey
   */
  #sortReviews(reviews, sortKey) {
    const sorted = [...reviews];

    switch (sortKey) {
      case 'highest_rating':
        sorted.sort((a, b) => b.rating - a.rating || b.date.localeCompare(a.date));
        break;
      case 'lowest_rating':
        sorted.sort((a, b) => a.rating - b.rating || b.date.localeCompare(a.date));
        break;
      case 'with_pictures':
        return sorted.filter((review) => review.images?.length);
      case 'pictures_first':
        sorted.sort((a, b) => {
          const aHas = a.images?.length ? 1 : 0;
          const bHas = b.images?.length ? 1 : 0;
          if (bHas !== aHas) return bHas - aHas;
          return b.date.localeCompare(a.date);
        });
        break;
      case 'created_at':
      default:
        sorted.sort((a, b) => b.date.localeCompare(a.date));
    }

    return sorted;
  }

  /**
   * @param {number} rating
   * @param {string} [sizeClass]
   */
  #renderStars(rating, sizeClass = '') {
    const filled = Math.min(5, Math.max(0, Math.round(Number(rating) || 0)));
    return renderStarRating(rating, sizeClass, this.#t('out_of_5_stars', { rating: filled }));
  }

  #renderAiSummary() {
    const showAiSummary = this.dataset.showAiSummary !== 'false';
    if (!showAiSummary) return '';

    const title = this.dataset.aiSummaryTitle || this.#aiSummary?.title || '';
    const text = this.dataset.aiSummaryText || this.#aiSummary?.text || '';
    const disclaimer = this.dataset.aiSummaryDisclaimer || this.#aiSummary?.disclaimer || '';

    if (!text.trim()) return '';

    return `
      <div class="customer-reviews__ai-summary">
        <div class="customer-reviews__ai-summary-header">
          <p class="customer-reviews__ai-summary-title">${this.#escapeHtml(title)}</p>
        </div>
        <p class="customer-reviews__ai-summary-text">${this.#escapeHtml(text)}</p>
        <div class="customer-reviews__ai-summary-disclaimer">
          <span class="customer-reviews__ai-sparkle" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 14 14">
              <path d="M12.0025 7.50059C12.0038 7.70445 11.9419 7.9037 11.8252 8.07091C11.7086 8.23813 11.543 8.36509 11.3513 8.43434L8.12754 9.62559L6.94004 12.8518C6.86972 13.0429 6.74249 13.2078 6.57552 13.3242C6.40854 13.4407 6.20987 13.5031 6.00629 13.5031C5.80271 13.5031 5.60404 13.4407 5.43707 13.3242C5.27009 13.2078 5.14286 13.0429 5.07254 12.8518L3.87754 9.62559L0.651291 8.43809C0.460247 8.36776 0.29537 8.24053 0.178907 8.07356C0.0624441 7.90659 0 7.70791 0 7.50434C0 7.30076 0.0624441 7.10208 0.178907 6.93511C0.29537 6.76814 0.460247 6.64091 0.651291 6.57059L3.87754 5.37559L5.06504 2.14934C5.13536 1.95829 5.26259 1.79342 5.42957 1.67695C5.59654 1.56049 5.79522 1.49805 5.99879 1.49805C6.20237 1.49805 6.40104 1.56049 6.56802 1.67695C6.73499 1.79342 6.86222 1.95829 6.93254 2.14934L8.12754 5.37559L11.3538 6.56309C11.5457 6.63296 11.7111 6.76067 11.8274 6.9286C11.9436 7.09653 12.0048 7.29639 12.0025 7.50059Z" fill="currentColor"/>
            </svg>
          </span>
          <span class="customer-reviews__ai-summary-disclaimer-text">${this.#escapeHtml(disclaimer)}</span>
        </div>
      </div>
    `;
  }

  /**
   * @param {string} name
   */
  #getInitial(name) {
    return getReviewInitial(name);
  }

  /**
   * @param {string} dateStr
   * @param {string} [className]
   */
  #renderDateHtml(dateStr, className = 'customer-reviews__review-date') {
    return renderReviewDateHtml(dateStr, this.dataset, (value) => this.#escapeAttr(value), className);
  }

  /**
   * @param {number} totalPages
   * @param {number} currentPage
   * @returns {(number | 'ellipsis')[]}
   */
  #buildPaginationPages(totalPages, currentPage) {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set([1, totalPages]);

    if (currentPage <= 3) {
      pages.add(2);
      pages.add(3);
      pages.add(4);
    } else if (currentPage >= totalPages - 2) {
      for (let page = totalPages - 3; page <= totalPages - 1; page += 1) {
        pages.add(page);
      }
    } else {
      pages.add(currentPage - 1);
      pages.add(currentPage);
      pages.add(currentPage + 1);
    }

    const sorted = [...pages].sort((a, b) => a - b);
    /** @type {(number | 'ellipsis')[]} */
    const result = [];

    for (let index = 0; index < sorted.length; index += 1) {
      const page = sorted[index];
      if (index > 0 && page - sorted[index - 1] > 1) {
        result.push('ellipsis');
      }
      result.push(page);
    }

    return result;
  }

  /**
   * @param {number} totalPages
   * @param {number} currentPage
   */
  #renderPagination(totalPages, currentPage) {
    if (totalPages <= 1) return '';

    const pages = this.#buildPaginationPages(totalPages, currentPage);

    const pageButtons = pages
      .map((page) => {
        if (page === 'ellipsis') {
          return '<span class="customer-reviews__pagination-ellipsis">…</span>';
        }

        const isActive = page === currentPage;
        return `<button type="button" class="customer-reviews__pagination-page${isActive ? ' customer-reviews__pagination-page--active' : ''}" data-page="${page}" aria-label="${this.#escapeAttr(this.#t('pagination_goto_number', { number: page }))}"${isActive ? ' aria-current="page"' : ''}>${page}</button>`;
      })
      .join('');

    return `
      <nav class="customer-reviews__pagination" aria-label="${this.#escapeAttr(this.#t('pagination_nav'))}">
        <button type="button" class="customer-reviews__pagination-nav" data-page="prev" aria-label="${this.#escapeAttr(this.#t('pagination_prev'))}"${currentPage === 1 ? ' disabled' : ''}>
          <svg width="7" height="12" viewBox="0 0 7 12" fill="none" aria-hidden="true"><path d="M2.454 6L6.527 10.073a.848.848 0 01-.213 1.322.848.848 0 01-1.054-.213L.979 6.633a.848.848 0 010-1.266L5.26.872A.848.848 0 016.314.66a.848.848 0 01.213 1.322L2.454 6z" fill="currentColor"/></svg>
        </button>
        ${pageButtons}
        <button type="button" class="customer-reviews__pagination-nav" data-page="next" aria-label="${this.#escapeAttr(this.#t('pagination_next'))}"${currentPage === totalPages ? ' disabled' : ''}>
          <svg width="7" height="12" viewBox="0 0 7 12" fill="none" aria-hidden="true"><path d="M4.946 6L.873 1.927A.848.848 0 01.66.873.848.848 0 01.872.66a.848.848 0 011.322.213L5.367 5.367a.848.848 0 010 1.266L2.194 10.872a.848.848 0 01-1.322.213.848.848 0 01-.213-1.054L4.946 6z" fill="currentColor"/></svg>
        </button>
      </nav>
    `;
  }

  #render() {
    const heading = this.dataset.heading || '';
    const perPage = Number(this.dataset.perPage) || 5;
    const showPhotoGrid = this.dataset.showPhotoGrid !== 'false';
    const showAskQuestion = this.dataset.showAskQuestion !== 'false';
    const buttonColor = this.dataset.buttonColor || '';
    const starColor = this.dataset.starColor || '';

    const stats = this.#computeStats(this.#allReviews);
    const reviewCountLabel = stats.total === 1 ? this.#t('review_singular') : this.#t('review_plural');
    const filtered = this.#filterReviews(this.#allReviews);
    const sorted = this.#sortReviews(filtered, this.#sortKey);
    const sortedQuestions = this.#sortQuestions(this.#allQuestions);
    const isReviewsTab = this.#activeTab === 'reviews';
    const activeList = isReviewsTab ? sorted : sortedQuestions;
    const totalPages = Math.max(1, Math.ceil(activeList.length / perPage));
    this.#currentPage = Math.min(this.#currentPage, totalPages);

    const start = (this.#currentPage - 1) * perPage;
    const pageReviews = isReviewsTab ? sorted.slice(start, start + perPage) : [];
    const pageQuestions = isReviewsTab ? [] : sortedQuestions.slice(start, start + perPage);
    const hasQuestions = this.#allQuestions.length > 0;
    const filterActive = this.#ratingFilter !== 'all';

    const histogramHtml = stats.histogram
      .map(
        ({ rating, frequency, percentage }) => `
        <div class="customer-reviews__histogram-row">
          ${this.#renderStars(rating, 'customer-reviews__star--sm')}
          <div class="customer-reviews__histogram-bar" aria-hidden="true">
            <div class="customer-reviews__histogram-fill" style="width: ${percentage}%;"></div>
          </div>
          <span class="customer-reviews__histogram-count">${frequency}</span>
        </div>
      `
      )
      .join('');

    const photoThumbsHtml = stats.photoGallery
      .slice(0, 8)
      .map(({ image, review }, index) => {
        const imageIndex = Math.max(0, review.images?.indexOf(image) ?? 0);
        return `
          <button type="button" class="customer-reviews__photo-thumb" data-lightbox-review-id="${this.#escapeAttr(review.id)}" data-lightbox-index="${imageIndex}" aria-label="${this.#escapeAttr(this.#t('view_review_photo', { n: index + 1 }))}">
            <img src="${this.#escapeAttr(image)}" alt="" loading="lazy" width="58" height="58">
          </button>
        `;
      })
      .join('');

    const photoGridHtml =
      showPhotoGrid && stats.photoGallery.length
        ? `
        <div class="customer-reviews__photo-grid-wrap">
          <div class="customer-reviews__photo-grid" aria-label="${this.#escapeAttr(this.#t('review_photos'))}">
            ${photoThumbsHtml}
          </div>
          <div class="customer-reviews__photo-carousel" data-photo-carousel>
            <button type="button" class="customer-reviews-lightbox__nav customer-reviews-lightbox__nav--prev customer-reviews__photo-carousel-nav" data-photo-carousel-prev aria-label="${this.#escapeAttr(this.#t('previous_photos'))}" disabled>
              ${CAROUSEL_NAV_PREV_SVG}
            </button>
            <div class="customer-reviews__photo-carousel-viewport" data-photo-carousel-viewport>
              <div class="customer-reviews__photo-carousel-track" data-photo-carousel-track aria-label="${this.#escapeAttr(this.#t('review_photos'))}">
                ${photoThumbsHtml}
              </div>
            </div>
            <button type="button" class="customer-reviews-lightbox__nav customer-reviews-lightbox__nav--next customer-reviews__photo-carousel-nav" data-photo-carousel-next aria-label="${this.#escapeAttr(this.#t('next_photos'))}">
              ${CAROUSEL_NAV_NEXT_SVG}
            </button>
          </div>
        </div>
      `
        : '';

    const sortOptionsHtml = this.#buildSortOptions()
      .map(
        (option) =>
          `<option value="${option.value}"${option.value === this.#sortKey ? ' selected' : ''}>${option.label}</option>`
      )
      .join('');

    const reviewsHtml = !isReviewsTab
      ? ''
      : pageReviews.length
        ? pageReviews
            .map((review) => {
              const imagesHtml = (review.images || [])
                .map(
                  (image, imageIndex) => `
                <button type="button" class="customer-reviews__review-image" data-lightbox-review-id="${this.#escapeAttr(review.id)}" data-lightbox-index="${imageIndex}" aria-label="${this.#escapeAttr(this.#t('view_review_image'))}">
                  <img src="${this.#escapeAttr(image)}" alt="" loading="lazy" width="80" height="80">
                </button>
              `
                )
                .join('');

              return `
              <article class="customer-reviews__item">
                <div class="customer-reviews__item-stars">
                  ${this.#renderStars(review.rating)}
                </div>
                <div class="customer-reviews__reviewer">
                  <span class="customer-reviews__avatar" aria-hidden="true">${this.#escapeHtml(this.#getInitial(review.author))}</span>
                  <div class="customer-reviews__reviewer-details">
                    <span class="customer-reviews__reviewer-name">${this.#escapeHtml(review.author)}</span>
                    ${this.#renderDateHtml(review.date)}
                  </div>
                </div>
                <div class="customer-reviews__body">${this.#escapeHtml(review.body)}</div>
                ${imagesHtml ? `<div class="customer-reviews__item-images">${imagesHtml}</div>` : ''}
              </article>
            `;
            })
            .join('')
        : filterActive
          ? `<p class="customer-reviews__empty">${this.#escapeHtml(this.#t('no_reviews_filter'))}</p>`
          : `<p class="customer-reviews__empty">${this.#escapeHtml(this.#t('no_reviews'))}</p>`;

    const questionsHtml = this.#renderQuestionsList(pageQuestions);

    const buttonStyle = buttonColor ? `--customer-reviews-btn-bg: ${buttonColor};` : '';
    const starStyle = starColor ? `--customer-reviews-star: ${starColor};` : '';
    const widgetStyle = `${buttonStyle}${starStyle}`;

    this.innerHTML = `
      <div class="customer-reviews__widget" style="${widgetStyle}">
        <header class="customer-reviews__header">
          <div class="customer-reviews__header-top">
            <h2 class="customer-reviews__title">${this.#escapeHtml(heading)}</h2>
            <div class="customer-reviews__summary">
              <span class="customer-reviews__average">${stats.average.toFixed(1)}</span>
              <span class="customer-reviews__count">${stats.total} ${this.#escapeHtml(reviewCountLabel)}</span>
            </div>
          </div>
          <div class="customer-reviews__header-main">
            <div class="customer-reviews__histogram">${histogramHtml}</div>
            ${photoGridHtml}
            <div class="customer-reviews__actions">
              <button type="button" class="customer-reviews__btn customer-reviews__btn--primary" data-action="write-review">${this.#escapeHtml(this.#t('write_review_btn'))}</button>
              ${showAskQuestion ? `<button type="button" class="customer-reviews__btn customer-reviews__btn--secondary" data-action="ask-question">${this.#escapeHtml(this.#t('ask_question_btn'))}</button>` : ''}
            </div>
          </div>
        </header>

        ${this.#renderAiSummary()}

        <div class="customer-reviews__toolbar">
          <div class="customer-reviews__tabs" role="tablist" aria-label="${this.#escapeAttr(this.#t('review_sections'))}">
            <button type="button" role="tab" aria-selected="${isReviewsTab ? 'true' : 'false'}" class="customer-reviews__tab${isReviewsTab ? ' customer-reviews__tab--active' : ''}" data-tab="reviews">${this.#escapeHtml(this.#t('reviews_tab', { count: stats.total }))}</button>
            ${hasQuestions ? `<button type="button" role="tab" aria-selected="${isReviewsTab ? 'false' : 'true'}" class="customer-reviews__tab${isReviewsTab ? '' : ' customer-reviews__tab--active'}" data-tab="questions">${this.#escapeHtml(this.#t('questions_tab', { count: this.#allQuestions.length }))}</button>` : ''}
          </div>
          <div class="customer-reviews__toolbar-controls${isReviewsTab ? '' : ' customer-reviews__toolbar-controls--hidden'}">
            <div class="customer-reviews__filter-wrap">
              <button type="button" class="customer-reviews__filter-btn${filterActive ? ' customer-reviews__filter-btn--active' : ''}" data-filter-toggle aria-expanded="${this.#filterOpen ? 'true' : 'false'}" aria-label="${this.#escapeAttr(this.#t('filter_reviews'))}">
                <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M4.67 4H11.34L8 8.2 4.67 4ZM2.84 3.74C4.18 5.47 6.67 8.67 6.67 8.67V12.67c0 .37.3.67.67.67h1.33c.37 0 .67-.3.67-.67V8.67s2.48-3.2 3.82-4.93c.34-.44.03-1.07-.52-1.07H3.36c-.55 0-.88.63-.52 1.07Z"/></svg>
                <span>${this.#escapeHtml(this.#t('filter_btn'))}</span>
              </button>
              ${this.#renderFilterPanel()}
            </div>
            <div class="customer-reviews__sort">
              <label class="visually-hidden" for="customer-reviews-sort-${this.id || 'widget'}">${this.#escapeHtml(this.#t('sort_reviews_by'))}</label>
              <select id="customer-reviews-sort-${this.id || 'widget'}" class="customer-reviews__sort-select" data-sort-select>
                ${sortOptionsHtml}
              </select>
              <svg class="customer-reviews__sort-chevron" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M11.06 5.53 8 8.58 4.94 5.53 4 6.47l4 4 4-4z"/></svg>
            </div>
          </div>
        </div>

        <div class="customer-reviews__list"${isReviewsTab ? '' : ' hidden'}>${reviewsHtml}</div>
        <div class="customer-reviews__questions"${isReviewsTab ? ' hidden' : ''}>${questionsHtml}</div>
        ${this.#renderPagination(totalPages, this.#currentPage)}
      </div>
    `;

    this.#bindEvents();
  }

  #handlePaginationClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('.customer-reviews__pagination [data-page]');
    if (!button || !this.contains(button)) return;
    if (button instanceof HTMLButtonElement && button.disabled) return;

    const page = button.getAttribute('data-page');
    if (!page) return;

    const perPage = Number(this.dataset.perPage) || 5;
    const isReviewsTab = this.#activeTab === 'reviews';
    const filtered = this.#filterReviews(this.#allReviews);
    const sorted = isReviewsTab ? this.#sortReviews(filtered, this.#sortKey) : this.#sortQuestions(this.#allQuestions);
    const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));

    if (page === 'prev') {
      this.#currentPage = Math.max(1, this.#currentPage - 1);
    } else if (page === 'next') {
      this.#currentPage = Math.min(totalPages, this.#currentPage + 1);
    } else {
      this.#currentPage = Math.min(totalPages, Math.max(1, Number(page) || 1));
    }

    this.#render();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  #bindEvents() {
    this.#uiAbortController?.abort();
    this.#uiAbortController = new AbortController();
    const { signal } = this.#uiAbortController;

    this.querySelector('[data-sort-select]')?.addEventListener(
      'change',
      (event) => {
        const target = /** @type {HTMLSelectElement} */ (event.target);
        this.#sortKey = target.value;
        this.#currentPage = 1;
        this.#render();
      },
      { signal }
    );

    this.querySelectorAll('[data-tab]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const tab = button.getAttribute('data-tab');
          if (tab === 'questions' && this.#allQuestions.length === 0) return;
          this.#activeTab = tab === 'questions' ? 'questions' : 'reviews';
          this.#currentPage = 1;
          this.#filterOpen = false;
          this.#render();
        },
        { signal }
      );
    });

    this.querySelector('[data-filter-toggle]')?.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
        this.#filterOpen = !this.#filterOpen;
        this.#render();
      },
      { signal }
    );

    this.querySelectorAll('[data-filter-value]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const value = button.getAttribute('data-filter-value') || 'all';
          this.#ratingFilter = value;
          this.#filterOpen = false;
          this.#currentPage = 1;
          this.#render();
        },
        { signal }
      );
    });

    document.addEventListener(
      'click',
      (event) => {
        if (!this.#filterOpen) return;
        const target = /** @type {Node | null} */ (event.target);
        if (target && this.contains(target)) {
          const inFilter = target instanceof Element && target.closest('[data-filter-panel], [data-filter-toggle]');
          if (inFilter) return;
        }
        this.#filterOpen = false;
        this.#render();
      },
      { signal }
    );

    this.querySelectorAll('[data-lightbox-review-id]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const reviewId = button.getAttribute('data-lightbox-review-id');
          const imageIndex = Number(button.getAttribute('data-lightbox-index') || 0);
          if (reviewId) this.#openLightbox(reviewId, imageIndex);
        },
        { signal }
      );
    });

    this.querySelector('[data-action="write-review"]')?.addEventListener(
      'click',
      () => {
        this.#openSubmitDialog('review');
      },
      { signal }
    );

    this.querySelector('[data-action="ask-question"]')?.addEventListener(
      'click',
      () => {
        this.#openSubmitDialog('question');
      },
      { signal }
    );

    this.#bindPhotoCarousel();
  }

  #bindPhotoCarousel() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const viewport = this.querySelector('[data-photo-carousel-viewport]');
    const track = this.querySelector('[data-photo-carousel-track]');
    const prev = this.querySelector('[data-photo-carousel-prev]');
    const next = this.querySelector('[data-photo-carousel-next]');
    if (!viewport || !track || !prev || !next || !this.#uiAbortController) return;

    const { signal } = this.#uiAbortController;
    const prevButton = /** @type {HTMLButtonElement} */ (prev);
    const nextButton = /** @type {HTMLButtonElement} */ (next);

    const updateNav = () => {
      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      prevButton.disabled = viewport.scrollLeft <= 1;
      nextButton.disabled = maxScroll <= 1 || viewport.scrollLeft >= maxScroll - 1;
    };

    /** @param {number} direction */
    const scrollStep = (direction) => {
      const slide = track.querySelector('.customer-reviews__photo-thumb');
      if (!slide) return;
      const gap = Number.parseFloat(getComputedStyle(track).gap || '0') || 0;
      viewport.scrollBy({ left: direction * (/** @type {HTMLElement} */ (slide).offsetWidth + gap), behavior: 'smooth' });
    };

    prevButton.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
        scrollStep(-1);
      },
      { signal }
    );

    nextButton.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
        scrollStep(1);
      },
      { signal }
    );

    viewport.addEventListener('scroll', updateNav, { signal, passive: true });

    const resizeObserver = new ResizeObserver(updateNav);
    resizeObserver.observe(viewport);
    signal.addEventListener('abort', () => resizeObserver.disconnect());

    updateNav();
  }

  /**
   * @param {'review' | 'question'} type
   */
  #openSubmitDialog(type) {
    const dialogId = type === 'question' ? 'customer-reviews-question-dialog' : 'customer-reviews-write-dialog';
    const dialogComponent = document.getElementById(dialogId);
    if (dialogComponent && 'showDialog' in dialogComponent) {
      /** @type {{ showDialog: () => void }} */ (dialogComponent).showDialog();
    }
  }

  /**
   * @param {string} reviewId
   * @param {number} imageIndex
   */
  #openLightbox(reviewId, imageIndex) {
    const review = this.#allReviews.find((item) => item.id === reviewId);
    if (!review?.images?.length) return;

    const lightbox = document.querySelector('customer-reviews-lightbox');
    if (lightbox && 'open' in lightbox) {
      /** @type {CustomerReviewsLightbox} */ (lightbox).open(review, imageIndex, {
        showDate: this.dataset.showDate,
        dateFormat: this.dataset.dateFormat,
      });
    }

    const dialogComponent = document.getElementById('customer-reviews-lightbox');
    if (dialogComponent && 'showDialog' in dialogComponent) {
      /** @type {{ showDialog: () => void }} */ (dialogComponent).showDialog();
    }
  }

  /**
   * @param {string} value
   */
  #escapeHtml(value) {
    return escapeHtml(value);
  }

  /**
   * @param {string} value
   */
  #escapeAttr(value) {
    return escapeAttr(value);
  }

  /**
   * @param {string} message
   */
  #renderError(message) {
    this.innerHTML = `<p class="customer-reviews__error">${this.#escapeHtml(message)}</p>`;
  }
}

if (!customElements.get('customer-reviews')) {
  customElements.define('customer-reviews', CustomerReviews);
}

/**
 * Judge.me-style review photo lightbox with carousel and review details.
 */
class CustomerReviewsLightbox extends Component {
  /** @type {Review | null} */
  #review = null;

  /** @type {number} */
  #imageIndex = 0;

  /** @type {{ showDate?: string; dateFormat?: string } | null} */
  #dateSettings = null;

  /** @type {AbortController | null} */
  #uiAbortController = null;

  /** @type {Record<string, string> | null} */
  #i18n = null;

  /** @returns {Record<string, string>} */
  #getI18n() {
    if (!this.#i18n) {
      this.#i18n = getCustomerReviewsI18n(this);
    }
    return this.#i18n;
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [replacements]
   */
  #t(key, replacements = {}) {
    return translateI18n(this.#getI18n(), key, replacements);
  }

  /**
   * @param {Review} review
   * @param {number} imageIndex
   * @param {{ showDate?: string; dateFormat?: string }} [options]
   */
  open(review, imageIndex = 0, options = {}) {
    if (!review?.images?.length) return;
    this.#review = review;
    this.#imageIndex = Math.min(Math.max(0, imageIndex), review.images.length - 1);
    this.#dateSettings = options;
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#uiAbortController?.abort();
  }

  #getDateSettings() {
    return {
      showDate: this.#dateSettings?.showDate ?? this.dataset.showDate,
      dateFormat: this.#dateSettings?.dateFormat ?? this.dataset.dateFormat,
    };
  }

  #render() {
    const review = this.#review;
    const images = review?.images || [];
    if (!review || !images.length) {
      this.innerHTML = '';
      return;
    }

    const currentImage = images[this.#imageIndex] || images[0];
    const hasMultiple = images.length > 1;
    const filled = Math.min(5, Math.max(0, Math.round(Number(review.rating) || 0)));
    const starsHtml = renderStarRating(review.rating, '', this.#t('out_of_5_stars', { rating: filled }));
    const thumbsHtml = images
      .map(
        (image, index) => `
          <button
            type="button"
            class="customer-reviews-lightbox__thumb${index === this.#imageIndex ? ' customer-reviews-lightbox__thumb--active' : ''}"
            data-lightbox-thumb="${index}"
            aria-label="${escapeAttr(this.#t('go_to_image', { n: index + 1 }))}"
            aria-selected="${index === this.#imageIndex ? 'true' : 'false'}"
          >
            <img src="${escapeAttr(image)}" alt="" loading="lazy" width="48" height="48">
          </button>
        `
      )
      .join('');

    this.innerHTML = `
      <div class="customer-reviews-lightbox__shell">
        <button type="button" class="customer-reviews-lightbox__close" data-lightbox-close aria-label="${escapeAttr(this.#t('close'))}">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M13.3 0.710703C12.91 0.320703 12.28 0.320703 11.89 0.710703L6.99997 5.5907L2.10997 0.700703C1.71997 0.310703 1.08997 0.310703 0.699971 0.700703C0.309971 1.0907 0.309971 1.7207 0.699971 2.1107L5.58997 7.0007L0.699971 11.8907C0.309971 12.2807 0.309971 12.9107 0.699971 13.3007C1.08997 13.6907 1.71997 13.6907 2.10997 13.3007L6.99997 8.4107L11.89 13.3007C12.28 13.6907 12.91 13.6907 13.3 13.3007C13.69 12.9107 13.69 12.2807 13.3 11.8907L8.40997 7.0007L13.3 2.1107C13.68 1.7307 13.68 1.0907 13.3 0.710703Z" fill="currentColor"/>
          </svg>
        </button>
        <div class="customer-reviews-lightbox__layout">
          <div class="customer-reviews-lightbox__media">
            <div class="customer-reviews-lightbox__stage" style="--lightbox-bg-image: url('${escapeAttr(currentImage)}');">
              <div class="customer-reviews-lightbox__backdrop" aria-hidden="true">
                <div class="customer-reviews-lightbox__blur"></div>
              </div>
              <figure class="customer-reviews-lightbox__figure">
                <img class="customer-reviews-lightbox__image" src="${escapeAttr(currentImage)}" alt="${escapeAttr(this.#t('review_photo_alt'))}" width="800" height="800">
              </figure>
              ${
                hasMultiple
                  ? `
              <button type="button" class="customer-reviews-lightbox__nav customer-reviews-lightbox__nav--prev" data-lightbox-prev aria-label="${escapeAttr(this.#t('previous'))}"${this.#imageIndex === 0 ? ' disabled' : ''}>
                <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M11.0019 0.984688C10.5119 0.494687 9.72189 0.494687 9.23189 0.984688L0.921895 9.29469C0.531895 9.68469 0.531895 10.3147 0.921895 10.7047L9.23189 19.0147C9.72189 19.5047 10.5119 19.5047 11.0019 19.0147C11.4919 18.5247 11.4919 17.7347 11.0019 17.2447L3.76189 9.99469L11.0119 2.74469C11.4919 2.26469 11.4919 1.46469 11.0019 0.984688Z" fill="currentColor"/></svg>
              </button>
              <button type="button" class="customer-reviews-lightbox__nav customer-reviews-lightbox__nav--next" data-lightbox-next aria-label="${escapeAttr(this.#t('next'))}"${this.#imageIndex === images.length - 1 ? ' disabled' : ''}>
                <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M0.998154 19.0147C1.48815 19.5047 2.27815 19.5047 2.76815 19.0147L11.0782 10.7047C11.4682 10.3147 11.4682 9.68469 11.0782 9.29469L2.76815 0.984688C2.27815 0.494687 1.48815 0.494687 0.998154 0.984688C0.508154 1.47469 0.508154 2.26469 0.998154 2.75469L8.23815 10.0047L0.988154 17.2547C0.508154 17.7347 0.508154 18.5347 0.998154 19.0147Z" fill="currentColor"/></svg>
              </button>
              <div class="customer-reviews-lightbox__thumbs" aria-label="${escapeAttr(this.#t('thumbnail_nav'))}">${thumbsHtml}</div>
              `
                  : ''
              }
            </div>
          </div>
          <div class="customer-reviews-lightbox__content">
            ${starsHtml}
            <div class="customer-reviews__reviewer">
              <span class="customer-reviews__avatar" aria-hidden="true">${escapeHtml(getReviewInitial(review.author))}</span>
              <div class="customer-reviews__reviewer-details">
                <span class="customer-reviews__reviewer-name">${escapeHtml(review.author)}</span>
                ${renderReviewDateHtml(review.date, this.#getDateSettings(), escapeAttr)}
              </div>
            </div>
            <p class="customer-reviews-lightbox__body">${escapeHtml(review.body)}</p>
          </div>
        </div>
      </div>
    `;

    this.#bindEvents();
  }

  /**
   * @param {Event} event
   */
  #stopLightboxEvent(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  #bindEvents() {
    this.#uiAbortController?.abort();
    this.#uiAbortController = new AbortController();
    const { signal } = this.#uiAbortController;

    this.querySelector('[data-lightbox-close]')?.addEventListener(
      'click',
      (event) => {
        this.#stopLightboxEvent(event);
        this.#closeDialog();
      },
      { signal }
    );

    this.querySelector('[data-lightbox-prev]')?.addEventListener(
      'click',
      (event) => {
        this.#stopLightboxEvent(event);
        if (this.#imageIndex > 0) {
          this.#imageIndex -= 1;
          this.#render();
        }
      },
      { signal }
    );

    this.querySelector('[data-lightbox-next]')?.addEventListener(
      'click',
      (event) => {
        this.#stopLightboxEvent(event);
        const images = this.#review?.images || [];
        if (this.#imageIndex < images.length - 1) {
          this.#imageIndex += 1;
          this.#render();
        }
      },
      { signal }
    );

    this.querySelectorAll('[data-lightbox-thumb]').forEach((button) => {
      button.addEventListener(
        'click',
        (event) => {
          this.#stopLightboxEvent(event);
          const index = Number(button.getAttribute('data-lightbox-thumb'));
          if (!Number.isNaN(index)) {
            this.#imageIndex = index;
            this.#render();
          }
        },
        { signal }
      );
    });

    this.closest('dialog')?.addEventListener(
      'keydown',
      (event) => {
        if (!(event instanceof KeyboardEvent)) return;
        const images = this.#review?.images || [];
        if (event.key === 'ArrowLeft' && this.#imageIndex > 0) {
          event.preventDefault();
          this.#imageIndex -= 1;
          this.#render();
        } else if (event.key === 'ArrowRight' && this.#imageIndex < images.length - 1) {
          event.preventDefault();
          this.#imageIndex += 1;
          this.#render();
        }
      },
      { signal }
    );
  }

  #closeDialog() {
    const dialogComponent = document.getElementById('customer-reviews-lightbox');
    if (dialogComponent && 'closeDialog' in dialogComponent) {
      /** @type {{ closeDialog: () => void }} */ (dialogComponent).closeDialog();
    }
  }
}

if (!customElements.get('customer-reviews-lightbox')) {
  customElements.define('customer-reviews-lightbox', CustomerReviewsLightbox);
}

/**
 * Review / question forms submitted through Shopify's contact endpoint.
 */
class CustomerReviewsForm extends Component {
  /** @type {Record<string, string> | null} */
  #i18n = null;

  /** @returns {Record<string, string>} */
  #getI18n() {
    if (!this.#i18n) {
      this.#i18n = getCustomerReviewsI18n(this);
    }
    return this.#i18n;
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [replacements]
   */
  #t(key, replacements = {}) {
    return translateI18n(this.#getI18n(), key, replacements);
  }

  connectedCallback() {
    super.connectedCallback();
    this.querySelector('form')?.addEventListener('submit', this.#handleSubmit);
    this.querySelectorAll('[data-star]').forEach((button) => {
      button.addEventListener('click', () => {
        const rating = button.getAttribute('data-star');
        const input = this.querySelector('[data-rating-field]');
        if (input instanceof HTMLInputElement && rating) {
          input.value = rating;
          this.#updateStarPicker(Number(rating));
          this.#setRatingError(false);
        }
      });
    });
    this.querySelectorAll('[data-close-dialog]').forEach((button) => {
      button.addEventListener('click', () => this.#closeParentDialog());
    });

    const dialog = this.closest('dialog');
    dialog?.addEventListener('close', () => this.#resetForm());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.querySelector('form')?.removeEventListener('submit', this.#handleSubmit);
  }

  #closeParentDialog() {
    const dialogComponent = this.closest('dialog-component');
    if (dialogComponent && 'closeDialog' in dialogComponent) {
      /** @type {{ closeDialog: () => void }} */ (dialogComponent).closeDialog();
    }
  }

  #resetForm() {
    const form = this.querySelector('form.customer-reviews-form');
    const success = this.querySelector('[data-form-success]');
    const error = this.querySelector('[data-form-error]');

    if (form instanceof HTMLFormElement) form.reset();
    if (success instanceof HTMLElement) success.hidden = true;
    if (error instanceof HTMLElement) error.hidden = true;
    if (form instanceof HTMLElement) form.hidden = false;
    this.#updateStarPicker(0);
    this.#setRatingError(false);
    this.#setSubmitting(false);
  }

  /**
   * @param {boolean} visible
   */
  #setRatingError(visible) {
    const error = this.querySelector('[data-rating-error]');
    if (error instanceof HTMLElement) error.hidden = !visible;
  }

  /**
   * @param {boolean} submitting
   */
  #setSubmitting(submitting) {
    const submit = this.querySelector('[type="submit"]');
    if (!(submit instanceof HTMLButtonElement)) return;

    if (!submit.dataset.submitLabelDefault) {
      submit.dataset.submitLabelDefault = submit.dataset.submitLabel || submit.textContent?.trim() || this.#t('submit_default');
    }

    submit.disabled = submitting;
    submit.textContent = submitting ? this.#t('submitting') : submit.dataset.submitLabelDefault;
  }

  /**
   * @param {Event} event
   */
  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = /** @type {HTMLFormElement | null} */ (event.target);
    if (!form) return;

    const formType = this.dataset.formType === 'question' ? 'question' : 'review';
    const messageInput = form.querySelector('[data-message-field]');
    const bodyInput = form.querySelector('[data-contact-body]');
    const ratingInput = form.querySelector('[data-rating-field]');
    const error = this.querySelector('[data-form-error]');

    if (!(messageInput instanceof HTMLTextAreaElement) || !(bodyInput instanceof HTMLInputElement)) return;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (formType === 'review') {
      if (!(ratingInput instanceof HTMLInputElement) || !ratingInput.value) {
        this.#setRatingError(true);
        return;
      }
    }

    this.#setRatingError(false);
    if (error instanceof HTMLElement) error.hidden = true;

    const productTitle = this.dataset.productTitle || 'Unknown product';
    const productUrl = this.dataset.productUrl || '';
    const message = messageInput.value.trim();
    const lines = [];

    if (formType === 'review') {
      lines.push('--- Product review submission ---', `Product: ${productTitle}`);
      if (productUrl) lines.push(`URL: ${productUrl}`);
      if (ratingInput instanceof HTMLInputElement) lines.push(`Rating: ${ratingInput.value}/5`);
      lines.push('', message);
    } else {
      lines.push('--- Product question submission ---', `Product: ${productTitle}`);
      if (productUrl) lines.push(`URL: ${productUrl}`);
      lines.push('', message);
    }

    bodyInput.value = lines.join('\n');

    this.#setSubmitting(true);

    try {
      const response = await fetch('/contact', {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
      });

      if (!response.ok && !response.redirected) {
        throw new Error('Contact form submission failed');
      }

      const success = this.querySelector('[data-form-success]');
      if (success instanceof HTMLElement) {
        form.hidden = true;
        success.hidden = false;
      }

      form.reset();
      this.#updateStarPicker(0);
    } catch {
      if (error instanceof HTMLElement) error.hidden = false;
    } finally {
      this.#setSubmitting(false);
    }
  };

  /**
   * @param {number} rating
   */
  #updateStarPicker(rating) {
    this.querySelectorAll('[data-star]').forEach((button) => {
      const star = Number(button.getAttribute('data-star'));
      button.classList.toggle('customer-reviews-form__star-btn--active', rating > 0 && star <= rating);
    });
  }
}

if (!customElements.get('customer-reviews-form')) {
  customElements.define('customer-reviews-form', CustomerReviewsForm);
}
